import {UseMutationResult, useMutation, useQuery, useQueryClient} from 'react-query';

import {
  ApiAlertConfig,
  CreateAlertConfigResponse,
  NewAlertConfig,
  createAlertConfig,
  deleteAlertConfig,
  listAlertConfigs,
  updateAlertConfig,
} from 'src/lib/api/alert_configs';
import {ApiNotificationPreference} from 'src/lib/api/preferences';

export function useAlertConfigs() {
  const {data: alertConfigs, isLoading} = useQuery(['alertConfigs'], () => listAlertConfigs(), {
    cacheTime: 0,
  });
  return {alertConfigs, isLoaded: !isLoading};
}

export function useCreateAlertConfigMutation(): UseMutationResult<
  CreateAlertConfigResponse,
  Error,
  NewAlertConfig,
  () => void
> {
  const queryClient = useQueryClient();

  return useMutation(
    (data) => {
      return createAlertConfig(data);
    },
    {
      onSuccess: (response: CreateAlertConfigResponse) => {
        const {alertConfig, notificationPreference} = response;
        queryClient.setQueryData(
          ['notificationPreferences', notificationPreference.type],
          (prefs: ApiNotificationPreference[] | undefined) => [
            ...(prefs || []),
            notificationPreference,
          ]
        );
        queryClient.setQueryData(['alertConfigs'], (alertConfigs: ApiAlertConfig[] | undefined) => [
          alertConfig,
          ...(alertConfigs || []),
        ]);
        queryClient.invalidateQueries(['notificationPreferences', notificationPreference.type]);
        queryClient.invalidateQueries(['alertConfigs']);
      },
    }
  );
}

type UpdateData = Pick<ApiAlertConfig, 'id' | 'properties' | 'siteIds'>;

export function useUpdateAlertConfigMutation(): UseMutationResult<
  ApiAlertConfig,
  Error,
  UpdateData,
  () => void
> {
  const queryClient = useQueryClient();

  return useMutation(
    (data) => {
      return updateAlertConfig(data['id'], data['properties'], data['siteIds']);
    },
    {
      onSuccess: (data: ApiAlertConfig) => {
        queryClient.setQueryData(['alertConfigs'], (alertConfigs: ApiAlertConfig[] | undefined) =>
          alertConfigs ? alertConfigs.map((c) => (c.id === data.id ? data : c)) : [data]
        );
        queryClient.invalidateQueries(['alertConfigs']);
      },
    }
  );
}

type DeleteData = Pick<ApiAlertConfig, 'id'>;

export function useDeleteAlertConfigMutation(): UseMutationResult<
  ApiAlertConfig,
  Error,
  DeleteData,
  () => void
> {
  const queryClient = useQueryClient();

  return useMutation(({id}: DeleteData) => deleteAlertConfig(id), {
    onSuccess: (deleted: ApiAlertConfig) => {
      queryClient.setQueryData(['alertConfigs'], (alertConfigs: ApiAlertConfig[] | undefined) => {
        if (!alertConfigs) return [];
        return alertConfigs.filter((p) => p.id !== deleted.id);
      });
      queryClient.invalidateQueries(['alertConfigs']);
    },
  });
}
